import * as THREE from "three";
import { mergeGeometries } from "three/addons/utils/BufferGeometryUtils.js";
import type { UnitType } from "./shared/types";

// Merge rigid parts per articulated joint, sharing geometry/material across the army.
const cache = new Map<string, THREE.BufferGeometry>();
const material = new THREE.MeshStandardMaterial({ vertexColors: true, roughness: .9 });
const skin = 0xe0b486, leather = 0x654532, wood = 0x996a3e, steel = 0xb1bdc2;
const dark = 0x303942, gold = 0xd9b661, cream = 0xe8dec0;

function joint(parent: THREE.Group, name: string, key: string, at: number[], build: (p: Parts) => void): THREE.Group {
  const group = new THREE.Group(); group.name = name; group.position.set(at[0], at[1], at[2]); parent.add(group);
  let geometry = cache.get(key);
  if (!geometry) {
    const parts = new Parts(); build(parts);
    geometry = mergeGeometries(parts.items)!;
    parts.items.forEach(g => g.dispose()); geometry.computeBoundingBox(); cache.set(key, geometry);
  }
  const mesh = new THREE.Mesh(geometry, material); mesh.castShadow = true;
  mesh.userData.sharedGeometry = true; mesh.raycast = () => {}; group.add(mesh);
  return group;
}

class Parts {
  items: THREE.BufferGeometry[] = [];
  add(g: THREE.BufferGeometry, c: THREE.Color | number, x = 0, y = 0, z = 0, rx = 0, ry = 0, rz = 0) {
    g.applyMatrix4(new THREE.Matrix4().compose(new THREE.Vector3(x,y,z),
      new THREE.Quaternion().setFromEuler(new THREE.Euler(rx,ry,rz)), new THREE.Vector3(1,1,1)));
    g.deleteAttribute("uv");
    const flat = g.index ? g.toNonIndexed() : g;
    const color = new THREE.Color(c), colors = new Float32Array(flat.getAttribute("position").count * 3);
    for (let i = 0; i < colors.length; i += 3) colors.set([color.r,color.g,color.b],i);
    flat.setAttribute("color",new THREE.BufferAttribute(colors,3)); this.items.push(flat);
    if (flat !== g) g.dispose();
  }
  box(w:number,h:number,d:number,c:THREE.Color|number,x=0,y=0,z=0,rx=0,ry=0,rz=0) {
    this.add(new THREE.BoxGeometry(w,h,d),c,x,y,z,rx,ry,rz);
  }
  rod(radius:number,length:number,c:THREE.Color|number,x=0,y=0,z=0,rx=0,ry=0,rz=0) {
    this.add(new THREE.CylinderGeometry(radius,radius,length,6),c,x,y,z,rx,ry,rz);
  }
}

export function createSoldier(type: UnitType, team: THREE.Color): THREE.Group {
  const root = new THREE.Group(); root.name = "unit-figure";
  const mounted = type === "scout" || type === "knight";
  const heavy = type === "knight" || type === "royal_guard";
  const guard = type === "royal_guard", worker = type === "villager", archer = type === "archer";
  const key = `${type}:${team.getHexString()}`;
  const rider = new THREE.Group(); rider.name = "rider"; root.add(rider);
  if (mounted) { root.add(createHorse(type,team)); rider.position.y = 1.02; }
  joint(rider,"torso",key+":body",[0,0,0],p=>{
    p.box(guard ? .9 : .64,.79,.43,heavy ? steel : team,0,1.18,0);
    p.box(.53,.65,.08,team,0,1.16,.255);
    p.box(.56,.3,.45,worker ? leather : team,0,.77,0);
    p.box(.68,.09,.47,leather,0,.97,0); p.box(.12,.12,.03,gold,0,.97,.25);
    p.box(.39,.4,.37,skin,0,1.8,.035);
    if (worker) {
      p.add(new THREE.CylinderGeometry(.46,.54,.1,10),gold,0,2.04,0);
      p.add(new THREE.CylinderGeometry(.26,.35,.24,8),0xe5c774,0,2.19,0);
      p.rod(.345,.07,leather,0,2.07,0);
      p.box(.34,.48,.04,cream,0,1.04,.305);
      p.box(.15,.26,.16,leather,.34,.82,0);
    } else if (archer) {
      p.add(new THREE.ConeGeometry(.37,.54,6),team,0,2.02,-.045);
      p.box(.29,.31,.02,dark,0,1.85,.224); p.box(.24,.21,.025,skin,0,1.82,.24);
      p.box(.6,.64,.09,team,0,1.36,-.29,.2);
      p.rod(.15,.76,leather,.27,1.37,-.39,.12,0,-.2);
      for(const x of [.19,.29,.38]) {
        p.rod(.022,.65,wood,x,1.78,-.4);
        p.box(.07,.13,.025,cream,x,2.05,-.4);
      }
    } else {
      p.add(new THREE.CylinderGeometry(.29,.34,.29,8),heavy ? steel : 0x89958d,0,2.02,0);
      p.box(.06,.35,.07,heavy ? gold : steel,0,1.91,.26);
      if(heavy) {
        p.box(.41,.1,.025,dark,0,1.87,.245);
        for(const s of [-1,1]) p.box(guard ? .37 : .28,.27,.54,steel,s*(guard ? .51 : .42),1.54,0);
        p.box(.38,.52,.06,team,0,1.23,-.28);
        p.box(.12,.5,.47,guard ? team : gold,0,2.28,-.07);
      }
    }
  });
  for(const side of [-1,1]) {
    joint(rider,side < 0 ? "leg-left" : "leg-right",key+`:leg${side}`,[side*(mounted ? .39 : .19),.81,0],p=>{
      p.box(.22,.55,.25,heavy ? steel : 0x544536,0,-.26,0);
      p.box(.24,.25,.36,leather,0,-.66,.055);
    });
    const arm = joint(rider,side < 0 ? "arm-left" : "arm-right",key+`:arm${side}`,[side*(guard ? .57 : .43),1.5,0],p=>{
      p.box(.25,.25,.28,heavy ? steel : team,0,-.11,0);
      if(worker) p.box(.27,.11,.3,cream,0,-.25,0);
      p.box(.2,.3,.21,heavy ? steel : skin,0,-.34,.025);
      p.box(.21,.17,.22,skin,0,-.52,.07);
      if(side > 0) {
        if(worker) {
          // Work props are separate so picking food can use bare hands.
        } else if(archer) {
          p.rod(.018,.93,wood,0,-.45,.46,Math.PI/2);
          p.add(new THREE.ConeGeometry(.05,.17,4),steel,0,-.45,.96,Math.PI/2);
        } else if(type === "spearman" || type === "skirmisher" || type === "knight") {
          const length = type === "spearman" ? 3.05 : type === "knight" ? 3.25 : 1.95;
          p.rod(.048,length,wood,.13,length/2-1.05,.22);
          p.add(new THREE.ConeGeometry(.13,.36,4),steel,.13,length-.89,.22);
          if(type === "knight") p.box(.33,.38,.025,team,.27,length-1.35,.22);
        } else {
          p.box(.13,guard ? 1.28 : .8,.07,steel,.1,.03,.2);
          p.box(.41,.08,.12,gold,.1,-.42,.2);
          p.rod(.055,.28,leather,.1,-.56,.2);
        }
      } else if(archer) {
        p.add(new THREE.TorusGeometry(.7,.075,4,12,Math.PI),wood,-.1,-.23,.4,0,-Math.PI/2,-Math.PI/2);
        p.rod(.018,1.4,cream,-.1,-.23,.4);
      } else if(type === "skirmisher" || type === "spearman") {
        const r = type === "skirmisher" ? .52 : .3;
        p.add(new THREE.CylinderGeometry(r,r,.13,10),leather,-.08,-.4,.26,Math.PI/2);
        p.add(new THREE.CylinderGeometry(r*.83,r*.83,.15,10),team,-.08,-.4,.28,Math.PI/2);
        p.add(new THREE.CylinderGeometry(r*.23,r*.23,.2,8),steel,-.08,-.4,.32,Math.PI/2);
      } else if(heavy) {
        const shield = new THREE.Shape(); shield.moveTo(-.34,.45);shield.lineTo(.34,.45);
        shield.lineTo(.3,-.26);shield.lineTo(0,-.65);shield.lineTo(-.3,-.26);shield.closePath();
        p.add(new THREE.ExtrudeGeometry(shield,{depth:.11,bevelEnabled:false}),team,-.07,-.35,.23);
        p.box(.08,.8,.025,gold,-.07,-.35,.356);p.box(.52,.07,.025,gold,-.07,-.18,.356);
      }
    });
    if (worker && side > 0) for (const tool of ["axe", "pickaxe", "hoe", "hammer"]) {
      const prop = joint(arm, `tool-${tool}`, `villager-tool:${tool}`, tool === "hammer" || tool === "pickaxe" ? [.06,-.52,.07] : [0,0,0], p => {
        if (tool === "hammer") {
          p.rod(.055,.7,wood,0,.3,0);
          p.box(.27,.24,.43,steel,0,.66,0);
          return;
        }
        if (tool === "pickaxe") {
          p.rod(.055,1.25,wood,0,.42,0);
          p.box(.12,.12,.65,steel,0,.94,0);
          for (const side of [-1,1]) p.add(new THREE.ConeGeometry(.085,.32,4),steel,0,.89,side*.43,side*1.9);
          return;
        }
        p.rod(.055, 1.25, wood, .06, -.08, .17, .18);
        if (tool === "axe") {
          p.box(.1,.28,.43,steel,.06,.44,.37,.18);
          p.box(.12,.3,.08,dark,.06,.44,.265,.18);
        } else if (tool === "hoe") {
          p.box(.36,.08,.43,steel,.06,.45,.35,.3);
        } else p.box(.27,.24,.43,steel,.06,.25,.17);
      });
      if (tool === "hammer" || tool === "pickaxe") prop.rotation.x = Math.PI / 2;
      prop.visible = false;
    }
  }
  return root;
}

function createHorse(type: UnitType, team: THREE.Color): THREE.Group {
  const root=new THREE.Group(); root.name="mounted-horse";
  const knight=type === "knight", key=`horse:${type}:${team.getHexString()}`;
  joint(root,"horse-body",key,[0,0,0],p=>{
    const coat=knight ? 0x605e55 : 0xa4774d;
    p.box(.66,.7,1.65,coat,0,1.05,0);
    p.box(.46,.96,.43,coat,0,1.55,.65,-.3);
    p.box(.44,.39,.62,coat,0,1.98,.86);
    p.box(.38,.26,.3,leather,0,1.86,1.2);
    for(const s of [-1,1]) p.add(new THREE.ConeGeometry(.085,.28,4),coat,s*.15,2.3,.69);
    p.box(.1,.72,.14,dark,0,1.73,.42,-.3);
    p.box(.12,.78,.13,dark,0,.86,-.88,.25);
    p.box(.74,knight ? .57 : .2,.96,team,0,knight ? 1.07 : 1.32,-.1);
    p.box(.55,.18,.61,leather,0,1.47,-.11);
    if(knight) {
      p.box(.56,.65,.14,steel,0,1.62,.9,-.3);
      p.box(.75,.38,.17,steel,0,1.13,.76);
      for(const s of [-1,1]) p.box(.035,.08,.82,gold,s*.385,.92,-.1);
    } else p.box(.48,.075,.065,leather,0,1.94,1.12);
  });
  for(const s of [-1,1]) for(const front of [-1,1])
    joint(root,`horse-leg-${s}-${front}`,key+`:leg${s}:${front}`,[s*.24,.9,front*.54],p=>{
      p.box(.18,.65,.2,leather,0,-.32,0);p.box(.2,.19,.27,dark,0,-.76,.025);
    });
  return root;
}

export function createBombardCannon(team: THREE.Color): THREE.Group {
  const root=new THREE.Group();root.name="bombard-cannon";const key=`cannon:${team.getHexString()}`;
  joint(root,"cannon-carriage",key,[0,0,0],p=>{
    p.box(1.36,.3,1.7,wood,0,.55,-.15);p.box(.34,.23,1.3,wood,0,.4,-1.2);
    for(const s of [-1,1]) {
      p.box(.14,.3,1.52,team,s*.63,.72,-.12);
      p.add(new THREE.CylinderGeometry(.6,.6,.22,12),dark,s*.82,.61,-.15,0,0,Math.PI/2);
      p.add(new THREE.CylinderGeometry(.44,.44,.24,10),wood,s*.82,.61,-.15,0,0,Math.PI/2);
      p.add(new THREE.CylinderGeometry(.16,.16,.27,8),team,s*.82,.61,-.15,0,0,Math.PI/2);
    }
  });
  joint(root,"cannon-barrel",key+":barrel",[0,1.05,0],p=>{
    p.add(new THREE.CylinderGeometry(.26,.34,2.35,12),dark,0,0,.47,Math.PI/2);
    for(const z of [-.45,.25,1.5]) p.add(new THREE.TorusGeometry(.3,.055,4,12),steel,0,0,z);
    p.add(new THREE.CylinderGeometry(.255,.255,.015,12),0x111619,0,0,1.66,Math.PI/2);
  });
  return root;
}

export function createOnager(team: THREE.Color): THREE.Group {
  const root=new THREE.Group();root.name="onager";const key=`onager:${team.getHexString()}`;
  joint(root,"onager-chassis",key,[0,0,0],p=>{
    p.box(1.45,.24,2.1,wood,0,.55,0);
    for(const side of [-1,1]) {
      p.box(.18,1.15,.24,wood,side*.55,1.05,.15);
      p.box(.12,.25,1.85,team,side*.73,.68,0);
      p.box(.16,.85,.16,wood,side*.55,.96,-.35,-.62);
      for(const z of [-.7,.7]) {
        p.add(new THREE.CylinderGeometry(.39,.39,.19,10),dark,side*.82,.4,z,0,0,Math.PI/2);
        p.add(new THREE.CylinderGeometry(.27,.27,.21,8),wood,side*.82,.4,z,0,0,Math.PI/2);
      }
    }
    p.rod(.2,1.45,cream,0,1.38,.15,0,0,Math.PI/2);
    p.box(1.5,.18,.18,dark,0,1.56,.15);
  });
  const arm=joint(root,"onager-arm",key+":arm",[0,1.25,.15],p=>{
    p.box(.2,1.75,.21,wood,0,.6,0);p.box(.55,.16,.5,wood,0,1.5,0);
  });
  arm.rotation.x=-1.15;
  joint(arm,"onager-loaded-stone","onager-stone",[0,1.75,0],p=>p.add(new THREE.DodecahedronGeometry(.3,0),0x989b8d));
  return root;
}

// Visual only: combat motion follows authoritative strike events, not attack orders.
export function animateUnitModel(model: THREE.Object3D, type: UnitType, state: string,
  seconds: number, phase: number, sinceShot: number, workActivity = "wood"): void {
  const mounted=type === "scout" || type === "knight";
  const moving=state === "moving", work=state === "gathering" || state === "building" || state === "repairing";
  const stride=Math.sin(seconds*(mounted ? 10 : 8)+phase);
  const strike=sinceShot >= 0 && sinceShot < .55 ? Math.sin(sinceShot/.55*Math.PI) : 0;
  const worker = type === "villager";
  const picking = worker && state === "gathering" && workActivity === "food";
  const farming = worker && state === "gathering" && workActivity === "farm";
  const mining = worker && state === "gathering" && (workActivity === "gold" || workActivity === "stone");
  if (worker) {
    const tool = state === "building" || state === "repairing" ? "hammer"
      : state === "attacking" ? "axe" : state !== "gathering" || picking ? ""
      : farming ? "hoe" : mining ? "pickaxe" : "axe";
    for (const name of ["axe", "pickaxe", "hoe", "hammer"]) {
      const prop = model.getObjectByName(`tool-${name}`);
      if (prop) prop.visible = name === tool;
    }
  }
  for(const side of [-1,1]) {
    const leg=model.getObjectByName(side < 0 ? "leg-left" : "leg-right");
    const arm=model.getObjectByName(side < 0 ? "arm-left" : "arm-right");
    if(leg) {leg.rotation.x=moving && !mounted ? stride*side*.65 : mounted ? -.35 : 0;leg.rotation.z=mounted ? -side*.15 : 0;}
    if(arm) {
      arm.rotation.set(moving ? -stride*side*.32 : 0,0,side*.06);arm.position.z=0;
      if(type === "skirmisher" && side > 0) arm.rotation.x=.38;
      if(work) arm.rotation.x=side > 0 ? -1.1 + Math.sin(seconds*7+phase)*1.05 : -.55;
      if (worker && (state === "building" || state === "repairing"))
        arm.rotation.x = side > 0 ? -.7 + Math.sin(seconds * 5 + phase) * .4 : -.45;
      if (picking) {
        const reach = Math.sin(seconds * 3.8 + phase + (side < 0 ? Math.PI : 0));
        arm.rotation.x = -.8 + reach * .38;
        arm.rotation.y = side * (.15 + reach * .12);
        arm.position.z = .12 + reach * .1;
      } else if (farming) {
        arm.rotation.x = -.65 + Math.sin(seconds * 4.2 + phase) * .5;
        arm.rotation.y = side * -.15;
      } else if (mining && side > 0) arm.rotation.x = -.35 + Math.sin(seconds * 5 + phase) * .6;
      if(state === "attacking") {
        if(type === "archer") { arm.rotation.x=side > 0 ? -1.15 : -.08;arm.rotation.y=side > 0 ? -.45+strike*.7 : .15;arm.position.z=side < 0 ? .35 : .15; }
        else if(side > 0) {arm.rotation.x=type === "skirmisher" ? .38+strike*1.1 : type === "spearman" || type === "knight" ? strike*1.4 : -strike*1.55;arm.position.z=strike*.22;}
        else arm.rotation.x=-strike*.3;
      }
    }
    for(const front of [-1,1]) {
      const leg=model.getObjectByName(`horse-leg-${side}-${front}`);
      if(leg) leg.rotation.x=moving ? Math.sin(seconds*10+phase+side*front*Math.PI/2)*.65 : 0;
    }
  }
  const torso=model.getObjectByName("torso");
  if(torso) torso.rotation.x=work ? .16+Math.sin(seconds*7+phase)*.1 : 0;
  if (torso && (picking || farming)) torso.rotation.x = (farming ? .23 : .08) + Math.sin(seconds * 4 + phase) * .05;
  const barrel=model.getObjectByName("cannon-barrel");
  if(barrel) barrel.position.z=-strike*.28;
  const arm=model.getObjectByName("onager-arm");
  if(arm) {
    const swing=sinceShot >= 0 && sinceShot < .18 ? sinceShot/.18 : Math.max(0,1-(sinceShot-.18)/1.2);
    arm.rotation.x=-1.15+(sinceShot >= 0 ? swing : 0)*1.8;
    const stone=arm.getObjectByName("onager-loaded-stone");if(stone)stone.visible=sinceShot > 1.4 || sinceShot < 0;
  }
}
